import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const CAT_META = {
  tarea:   { label: "Tarea",  cls: "bg-indigo-100 text-indigo-800" },
  mail:    { label: "Mail",   cls: "bg-yellow-100 text-yellow-900" },
  reunion: { label: "Reunión", cls: "bg-pink-100 text-pink-900" },
};

const WHO_META = {
  edu:    { initials: "E",  cls: "bg-[#941620] text-white" },
  manuel: { initials: "M",  cls: "bg-[#26775E] text-white" },
  both:   { initials: "EM", cls: "text-white", style: { background: "linear-gradient(90deg,#941620 50%,#26775E 50%)" } },
};

function AssigneeBadge({ assignees }) {
  if (!assignees || assignees.length === 0) return <span className="w-6 h-6 rounded-full bg-stone-200"></span>;
  const key = assignees.length > 1 ? "both" : assignees[0];
  const m = WHO_META[key] || { initials: assignees[0]?.[0]?.toUpperCase() || "?", cls: "bg-stone-500 text-white" };
  return (
    <span className={`inline-flex items-center justify-center rounded-full w-6 h-6 text-[9px] font-bold ${m.cls}`} style={m.style || {}}>
      {m.initials}
    </span>
  );
}

function dueChip(t) {
  const today = new Date().toISOString().slice(0, 10);
  if (t.due_date) {
    if (t.due_date < today) return { label: "VENCE", cls: "text-red-700" };
    if (t.due_date === today) return { label: "HOY", cls: "text-red-700" };
    return { label: t.due_date.slice(5), cls: "text-amber-700" };
  }
  if (t.due_week) return { label: t.due_week.length > 14 ? "Esta semana" : t.due_week, cls: "text-amber-700" };
  if (t.due_relative) return { label: t.due_relative.slice(0, 18), cls: "text-stone-500" };
  return { label: "", cls: "text-stone-400" };
}

export default function InicioPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ today: [], counts: { today: 0, this_week: 0, esperando: 0, next_meeting: null } });
  const [projects, setProjects] = useState([]);
  const [scope, setScope] = useState("mias");   // mias | manuel | comunes | todas
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [sum, prj] = await Promise.all([
        api.get("/tasks/summary/today"),
        api.get("/icaa/projects").catch(() => ({ data: [] })),
      ]);
      setData(sum.data);
      setProjects(Array.isArray(prj.data) ? prj.data : (prj.data?.projects || []));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const projectTitle = (pid) => projects.find(p => p.id === pid)?.title || "";

  const filtered = data.today.filter(t => {
    if (scope === "todas") return true;
    if (scope === "mias") return t.assignees?.includes("edu") || t.assignees?.length === 0;
    if (scope === "manuel") return t.assignees?.includes("manuel");
    if (scope === "comunes") return (t.assignees?.length || 0) >= 2;
    return true;
  });

  const toggleDone = async (task) => {
    const newStatus = task.status === "resuelta" ? "pendiente" : "resuelta";
    try {
      await api.patch(`/tasks/${task.id}`, { status: newStatus });
      // remove locally if now resuelta
      setData(d => ({ ...d, today: d.today.map(t => t.id === task.id ? { ...t, status: newStatus } : t) }));
    } catch (e) { /* ignore */ }
  };

  const today = new Date().toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "short", year: "numeric" });

  return (
    <div className="min-h-screen bg-gb-bg p-6" data-testid="inicio-page">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Cabecera */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.4em] text-stone-500">{today}</p>
            <h1 className="font-serif text-3xl mt-1">Buenos días, {user?.name?.split(" ")[0] || "Eduardo"}</h1>
          </div>
          <div className="text-right">
            <p className="font-serif text-5xl text-[#941620]" data-testid="inicio-count-today">{data.counts.today}</p>
            <p className="text-[10px] uppercase tracking-widest text-stone-500 mt-0.5">para hoy</p>
          </div>
        </div>

        {/* Toggle scope */}
        <div className="inline-flex rounded-full border border-stone-300 bg-white p-1 text-[11px] uppercase tracking-[0.2em]">
          {[["mias", "Mías"], ["manuel", "De Manuel"], ["comunes", "Comunes"], ["todas", "Todas"]].map(([k, l]) => (
            <button key={k} onClick={() => setScope(k)}
                    data-testid={`inicio-scope-${k}`}
                    className={`rounded-full px-3 py-1 ${scope === k ? "bg-stone-900 text-[#FAFAF6]" : "text-stone-600 hover:bg-stone-100"}`}>{l}</button>
          ))}
        </div>

        {/* Lista limpia */}
        <div className="rounded-xl border border-stone-200 bg-white shadow-sm overflow-hidden">
          {loading && <div className="p-8 text-center text-stone-400">Cargando…</div>}
          {!loading && filtered.length === 0 && (
            <div className="p-10 text-center">
              <p className="font-serif text-2xl text-emerald-700">¡Nada pendiente para hoy!</p>
              <p className="text-sm text-stone-500 mt-1">Buen momento para trabajar en lo importante, no lo urgente.</p>
              <Link to="/tareas" className="mt-4 inline-block text-[11px] uppercase tracking-widest text-[#941620] font-semibold hover:underline">Ver todas las tareas →</Link>
            </div>
          )}
          <div className="divide-y divide-stone-100">
            {filtered.map(t => {
              const cat = CAT_META[t.category] || CAT_META.tarea;
              const due = dueChip(t);
              return (
                <div key={t.id} data-testid={`inicio-task-${t.id}`}
                     className="px-5 py-4 hover:bg-stone-50 flex items-start gap-3">
                  <input type="checkbox"
                         checked={t.status === "resuelta"}
                         onChange={() => toggleDone(t)}
                         data-testid={`inicio-done-${t.id}`}
                         className="mt-1 w-4 h-4 accent-[#941620] cursor-pointer" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      {t.project_id && (
                        <span className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
                          {projectTitle(t.project_id)}
                        </span>
                      )}
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cat.cls}`}>{cat.label}</span>
                    </div>
                    <p className={`text-[14px] font-medium leading-snug ${t.status === "resuelta" ? "line-through text-stone-400" : "text-stone-900"}`}>{t.title}</p>
                    {t.next_task && <p className="text-[11px] text-stone-500 mt-0.5">Sig: {t.next_task}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <AssigneeBadge assignees={t.assignees} />
                    <span className={`text-[10px] font-semibold ${due.cls}`}>{due.label}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer compacto con enlaces */}
          {!loading && (
            <div className="border-t border-stone-100 bg-stone-50/50 px-5 py-3 flex flex-wrap items-center justify-between gap-2 text-[11px]">
              <div className="flex gap-3 text-stone-500 flex-wrap">
                <span><strong className="text-blue-700">{data.counts.this_week}</strong> esta semana</span>
                <span>·</span>
                <span><strong className="text-purple-700">{data.counts.esperando}</strong> esperando respuesta</span>
                {data.counts.next_meeting && (
                  <>
                    <span>·</span>
                    <span>Próxima reunión <strong className="text-emerald-700">{data.counts.next_meeting.due_date}</strong></span>
                  </>
                )}
              </div>
              <Link to="/tareas" data-testid="inicio-view-all"
                    className="uppercase tracking-widest text-[#941620] font-semibold hover:underline">Ver todas →</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
